import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getTask, addTaskComment, deleteTaskComment, downloadAttachment, approveTask } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft, Calendar, Clock, User, Building, Tag, Flag,
  Paperclip, MessageSquare, Send, Trash2, FileText, Edit, ArrowRightLeft, UserCheck, Users, ShieldCheck, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const getStatusColor = (status) => {
  switch (status) {
    case 'Completed': case 'Done': return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20';
    case 'In Progress': return 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/20';
    case 'In Review': return 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/20';
    default: return 'bg-slate-500/15 text-slate-600 dark:text-slate-400 border border-slate-500/20';
  }
};

const getPriorityInfo = (priority, dept) => {
  const HIGH = { label: 'High', color: 'bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/20' };
  const MED = { label: 'Medium', color: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/20' };
  const LOW = { label: 'Low', color: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20' };
  // D&D uses a 1-5 scale (1=High, 2-3=Medium, 4-5=Low); other depts use 1-10 (1-3 High, 4-6 Medium, 7-10 Low)
  if (dept === 'Design & Development') {
    if (priority === 1) return HIGH;
    if (priority <= 3) return MED;
    return LOW;
  }
  if (priority <= 3) return HIGH;
  if (priority <= 6) return MED;
  return LOW;
};

export const TaskDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approving, setApproving] = useState(false);

  const isApprovalDept = ['Design & Development', 'Marketing', 'Sales Coordinators'].includes(task?.department);
  const canApprove = isApprovalDept && task?.status === 'In Review' && (
    (task?.approvers || []).length
      ? (task.approvers || []).includes(user?.id)
      : user?.role === 'Admin'
  );

  const handleApproval = async (action) => {
    setApproving(true);
    try {
      await approveTask(id, { action, reason: rejectReason });
      toast.success(action === 'approve' ? 'Task approved!' : 'Task rejected');
      setRejectReason('');
      fetchTask();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally { setApproving(false); }
  };

  useEffect(() => {
    fetchTask();
  }, [id]);

  const fetchTask = async () => {
    setLoading(true);
    try {
      const response = await getTask(id);
      setTask(response.data);
    } catch (error) {
      toast.error('Failed to load task');
      navigate('/tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setSubmittingComment(true);
    try {
      await addTaskComment(id, { text: commentText.trim() });
      setCommentText('');
      fetchTask();
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteTaskComment(id, commentId);
      fetchTask();
      toast.success('Comment deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete comment');
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await downloadAttachment(id, attachment.id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download attachment');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
      </div>
    );
  }

  if (!task) return null;

  const priorityInfo = getPriorityInfo(task.priority, task.department);
  const isCreator = task.created_by === user?.id;
  const isOverdue = (() => {
    if (task.status === 'Completed' || task.status === 'Cancelled' || task.status === 'Done') return false;
    const due = new Date(task.due_date);
    due.setHours(23, 59, 59, 999);
    return new Date() > due;
  })();

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="task-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/tasks" data-testid="back-to-tasks">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-bold" data-testid="task-title">{task.task_name}</h1>
            <p className="text-sm text-muted-foreground">
              Created by {task.created_by_name} on {format(new Date(task.created_at), 'PPP')}
            </p>
          </div>
        </div>
        <Button variant="outline" asChild data-testid="edit-task-btn">
          <Link to={`/tasks/${id}/edit`}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-lg">Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap" data-testid="task-description">
                {task.description || 'No description provided.'}
              </p>
            </CardContent>
          </Card>

          {/* Attachments */}
          {task.attachments && task.attachments.length > 0 && (
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Paperclip className="h-4 w-4" />
                  Attachments ({task.attachments.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {task.attachments.map((att) => (
                    <div
                      key={att.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-accent/50 hover:bg-accent transition-colors cursor-pointer"
                      onClick={() => handleDownloadAttachment(att)}
                      data-testid={`attachment-${att.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{att.filename}</p>
                          <p className="text-xs text-muted-foreground">{(att.size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Reassignment History */}
          {task.reassignment_history && task.reassignment_history.length > 0 && (
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ArrowRightLeft className="h-4 w-4" />
                  Reassignment History ({task.reassignment_history.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative pl-4 space-y-4">
                  <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-border" />
                  {task.reassignment_history.slice().reverse().map((entry) => (
                    <div key={entry.id} className="relative flex gap-3" data-testid={`reassign-${entry.id}`}>
                      <div className="absolute -left-[9px] top-1.5 h-2.5 w-2.5 rounded-full bg-[#F5A623] border-2 border-background" />
                      <div className="ml-3">
                        <p className="text-sm">
                          <span className="font-medium">{entry.old_assignee_name}</span>
                          <span className="text-muted-foreground mx-1.5">→</span>
                          <span className="font-medium">{entry.new_assignee_name}</span>
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Changed by {entry.changed_by_name} · {format(new Date(entry.timestamp), 'MMM d, yyyy h:mm a')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Comments */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Comments ({task.comments?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add Comment */}
              <form onSubmit={handleAddComment} className="flex gap-3" data-testid="comment-form">
                <Avatar className="h-8 w-8 bg-[#F5A623]/20 shrink-0 mt-1">
                  <AvatarFallback className="text-[#F5A623] text-xs font-semibold">
                    {user?.full_name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 space-y-2">
                  <Textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Write a comment..."
                    rows={2}
                    className="resize-none"
                    data-testid="comment-input"
                  />
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      size="sm"
                      disabled={!commentText.trim() || submittingComment}
                      className="bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold"
                      data-testid="submit-comment-btn"
                    >
                      <Send className="h-3.5 w-3.5 mr-1.5" />
                      {submittingComment ? 'Posting...' : 'Post'}
                    </Button>
                  </div>
                </div>
              </form>

              {task.comments && task.comments.length > 0 && <Separator />}

              {/* Comments List */}
              <div className="space-y-4">
                {(task.comments || []).slice().reverse().map((comment) => (
                  <div key={comment.id} className="flex gap-3" data-testid={`comment-${comment.id}`}>
                    <Avatar className="h-8 w-8 bg-slate-200 dark:bg-slate-700 shrink-0 mt-0.5">
                      <AvatarFallback className="text-sm font-semibold">
                        {comment.user_name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold">{comment.user_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(comment.created_at), 'MMM d, yyyy h:mm a')}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{comment.text}</p>
                    </div>
                    {(comment.user_id === user?.id || user?.role === 'Super Admin' || user?.role === 'Admin') && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0 h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDeleteComment(comment.id)}
                        data-testid={`delete-comment-${comment.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {(!task.comments || task.comments.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-4">No comments yet. Be the first to comment.</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Task Details */}
        <div className="space-y-6">
          {/* Approval Card (D&D tasks in review) */}
          {isApprovalDept && (task.status === 'In Review' || task.approval_status) && (
            <Card className="border-amber-500/30 bg-amber-50/30 dark:bg-amber-900/10" data-testid="approval-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-amber-600" />
                  Approval
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {task.status === 'In Review' && (
                  <Badge className="bg-amber-500/15 text-amber-600 border border-amber-500/20">Pending Approval</Badge>
                )}
                {task.approval_status === 'approved' && (
                  <div>
                    <Badge className="bg-emerald-500/15 text-emerald-600 border border-emerald-500/20">Approved</Badge>
                    <p className="text-xs text-muted-foreground mt-1">By {task.approved_by_name} on {task.approval_date ? format(new Date(task.approval_date), 'PPP') : ''}</p>
                  </div>
                )}
                {task.approval_status === 'rejected' && (
                  <div>
                    <Badge className="bg-red-500/15 text-red-600 border border-red-500/20">Rejected</Badge>
                    <p className="text-xs text-muted-foreground mt-1">By {task.approved_by_name}</p>
                    {task.rejection_reason && <p className="text-sm mt-1 italic">"{task.rejection_reason}"</p>}
                  </div>
                )}
                {canApprove && (
                  <div className="space-y-2 pt-2">
                    <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" size="sm" onClick={() => handleApproval('approve')} disabled={approving} data-testid="approve-btn">
                      <ShieldCheck className="h-4 w-4 mr-2" /> Approve
                    </Button>
                    <Textarea placeholder="Reason for rejection (optional)" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={2} className="text-sm" data-testid="reject-reason" />
                    <Button variant="destructive" className="w-full" size="sm" onClick={() => handleApproval('reject')} disabled={approving} data-testid="reject-btn">
                      <XCircle className="h-4 w-4 mr-2" /> Reject
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="text-lg">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status</span>
                <Badge className={getStatusColor(task.status)} data-testid="task-status">{task.status}</Badge>
              </div>

              {/* Work Type — D&D only */}
              {task.work_type === 'Team' && task.department === 'Design & Development' && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Work Type</span>
                  <Badge className="bg-blue-500/15 text-blue-600 border border-blue-500/20"><Users className="h-3 w-3 mr-1 inline" /> Team</Badge>
                </div>
              )}

              {/* Priority */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Priority</span>
                <div className="flex items-center gap-2">
                  <Badge className={priorityInfo.color} data-testid="task-priority">{priorityInfo.label} ({task.priority})</Badge>
                </div>
              </div>

              <Separator />

              {/* Due Date */}
              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Due Date</p>
                  <p className={`text-sm font-medium ${isOverdue ? 'text-red-500' : ''}`} data-testid="task-due-date">
                    {format(new Date(task.due_date), 'PPP')}
                    {isOverdue && <span className="text-xs ml-1">(Overdue)</span>}
                  </p>
                </div>
              </div>

              {/* Assigned To */}
              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Assigned To</p>
                  {task.work_type === 'Team' && task.department === 'Design & Development' && task.assigned_to_names && task.assigned_to_names.length > 0 ? (
                    <div className="space-y-0.5">
                      {task.assigned_to_names.map((name, i) => {
                        const memberId = (task.assigned_to_list || [])[i];
                        const isSelf = memberId === user?.id;
                        return (
                          <p key={i} className="text-sm font-medium flex items-center gap-1.5" data-testid={`team-member-${i}`}>
                            {name}
                            {isSelf && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F5A623]/15 text-[#F5A623] font-semibold">You</span>}
                          </p>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm font-medium" data-testid="task-assignee">{task.assigned_to_name || '-'}</p>
                  )}
                </div>
              </div>

              {/* Assigned By (creator) */}
              <div className="flex items-center gap-3">
                <UserCheck className="h-4 w-4 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Assigned By</p>
                  <p className="text-sm font-medium" data-testid="task-assigned-by">{task.created_by_name || '-'}</p>
                </div>
              </div>

              {/* Reassigned From */}
              {task.reassigned_from_name && (
                <div className="flex items-center gap-3">
                  <ArrowRightLeft className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Reassigned From</p>
                    <p className="text-sm font-medium" data-testid="task-reassigned-from">{task.reassigned_from_name}</p>
                  </div>
                </div>
              )}

              {/* Department */}
              <div className="flex items-center gap-3">
                <Building className="h-4 w-4 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Department</p>
                  <p className="text-sm font-medium" data-testid="task-department">{task.department}</p>
                </div>
              </div>

              <Separator />

              {/* Category */}
              <div className="flex items-center gap-3">
                <Tag className="h-4 w-4 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Category</p>
                  <p className="text-sm font-medium">{task.category}</p>
                </div>
              </div>

              {/* Brand */}
              {task.brand && (
                <div className="flex items-center gap-3">
                  <Flag className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Brand</p>
                    <p className="text-sm font-medium">{task.brand}</p>
                  </div>
                </div>
              )}

              {/* Project */}
              {task.project_name && (
                <div className="flex items-center gap-3">
                  <Building className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Project</p>
                    <p className="text-sm font-medium">{task.project_name}</p>
                  </div>
                </div>
              )}

              <Separator />

              {/* Timestamps */}
              <div className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Last Updated</p>
                  <p className="text-sm font-medium">
                    {format(new Date(task.updated_at), 'PPP p')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
